// ── Principals / act-on-behalf contract (REQ-20, REQ-9) ─────────────────────
// A logged-in user can act on behalf of the customers they're authorized for
// (authorization comes from the ERP now; suomi.fi valtuudet — REQ-9 — adds more
// entries to the same list later, no FE change). The backend derives the list
// per user and re-authorizes the `X-On-Behalf-Of` header on every request. This
// module is a LEAF (no client/store imports) so it's cheap to unit-test:
// - `listPrincipals()` is the one mock endpoint (go-live = swap the body);
// - `getActivePrincipalId`/`setActivePrincipalId` persist the selection in
//   localStorage (the store + client wire the reactive UI + request header);
// - `withPrincipal(key)` prefixes every principal-scoped react-query key so
//   caches never leak across principals.
import { sleep, MockDomainError } from 'src/api/_mock/util';
import { isDemoMode } from 'src/api/state';

export class PrincipalError extends MockDomainError {}

export type PrincipalSource = 'self' | 'erp' | 'suomifi';
export type PrincipalCustomerType =
  | 'private'
  | 'housing_association'
  | 'business'
  | 'municipality'
  | 'shared_use';

export type Principal = {
  id: string;
  displayName: string;
  customerType: PrincipalCustomerType;
  source: PrincipalSource;
  address?: string;
  propertyId?: string;
};

export type PrincipalsResult = {
  self: Principal; // always present (who you are)
  principals: Principal[]; // others you can act as (may be empty)
};

// ── Dark-state mock ───────────────────────────────────────────────────────────
// Pre-swap this domain must be INVISIBLE: no fabricated people, no fabricated
// "self" identity. An empty `principals[]` is the documented dark state
// (docs/auth/golive-readiness.md) — `PrincipalSwitcher` returns null on
// `principals.length === 0`, so nothing renders and `ActingAsBanner` can never
// activate. `self` is still required by the type, so it carries only the stable
// `'self'` id and an empty display name; the only render site for that name is
// inside the switcher's dropdown, which is unreachable while the list is empty.
const SELF: Principal = {
  id: 'self',
  displayName: '',
  customerType: 'private',
  source: 'self',
};

// DEMO MODE: a populated act-on-behalf list so the PrincipalSwitcher +
// ActingAsBanner render and are usable in the PJH demo (Finnish labels). Gated
// on isDemoMode() — inert (empty dark state) in every normal build.
const DEMO_SELF: Principal = {
  id: 'self',
  displayName: 'Anna Virtanen',
  customerType: 'private',
  source: 'self',
};

const DEMO_PRINCIPALS: Principal[] = [
  {
    id: 'princ-asoy',
    displayName: 'Asunto Oy Kauppakatu 12',
    customerType: 'housing_association',
    source: 'erp',
    address: 'Kauppakatu 12, 15140 Lahti',
  },
  {
    id: 'princ-yritys',
    displayName: 'Virtanen Kahvila Oy',
    customerType: 'business',
    source: 'erp',
    address: 'Aleksanterinkatu 5, 15110 Lahti',
  },
  {
    id: 'princ-taloyhtio',
    displayName: 'Asunto Oy Lahden Puistola',
    customerType: 'housing_association',
    source: 'suomifi',
    address: 'Puistokatu 3, 15100 Lahti',
  },
];

export async function listPrincipals(): Promise<PrincipalsResult> {
  // Real: return (await apiClient.get('me/principals')).data;
  await sleep(200);
  if (isDemoMode()) {
    return { self: DEMO_SELF, principals: DEMO_PRINCIPALS };
  }
  return { self: SELF, principals: [] };
}

// ── Active-principal selection (persisted; null/"self" = acting as yourself) ──
const ACTIVE_PRINCIPAL_ID = 'ACTIVE_PRINCIPAL_ID';

export const getActivePrincipalId = (): string | null => {
  try {
    return localStorage.getItem(ACTIVE_PRINCIPAL_ID);
  } catch {
    return null;
  }
};

export const setActivePrincipalId = (id: string | null): void => {
  try {
    if (id && id !== 'self') localStorage.setItem(ACTIVE_PRINCIPAL_ID, id);
    else localStorage.removeItem(ACTIVE_PRINCIPAL_ID);
  } catch {
    /* localStorage unavailable — no-op */
  }
};

/**
 * Prefix a react-query key with the active principal so caches are isolated per
 * principal (switching never shows the previous principal's data). Acting as
 * self yields the stable `'self'` prefix. The server re-validates the header —
 * this is cache-scoping convenience, not security.
 */
export const withPrincipal = (key: readonly unknown[]): unknown[] => [
  getActivePrincipalId() ?? 'self',
  ...key,
];
